import { Bell, Loader2, X } from "lucide-react"
import { useNavigate, Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import type { DbNotification, EventInvitation } from "@/lib/api/notifications"
import { useAuthSession } from "@/hooks/use-auth-session"
import { useNotifications } from "@/hooks/use-notifications"
import { NotificationTypeIcon } from "@/lib/notification-icons"
import { formatDistanceToNow } from "date-fns"

function isUnread(notification: DbNotification): boolean {
  return !notification.read && !notification.read_at
}

function NotificationItem({
  notification,
  onSelect,
  onDelete,
  t,
}: {
  notification: DbNotification
  onSelect: () => void
  onDelete: () => void
  t: TFunction
}) {
  const unread = isUnread(notification)

  return (
    <div className={`relative group flex items-start gap-2 px-3 py-2.5 rounded-lg hover:bg-muted/80 transition-colors ${unread ? "bg-primary/5" : ""}`}>
      <button type="button" onClick={onSelect} className="flex flex-1 items-start gap-2 text-left min-w-0">
        <NotificationTypeIcon type={notification.type} />
        <div className="flex flex-col gap-0.5 min-w-0">
          <p className="text-sm font-medium line-clamp-1">{notification.title}</p>
          {notification.message && (
            <p className="text-xs text-muted-foreground line-clamp-2">{notification.message}</p>
          )}
          <p className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
          </p>
        </div>
      </button>
      <button
        type="button"
        aria-label={t("notifications.delete")}
        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-muted shrink-0"
        onClick={(e) => {
          e.stopPropagation()
          onDelete()
        }}
      >
        <X className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
    </div>
  )
}

function InvitationItem({ invitation, t }: { invitation: EventInvitation; t: TFunction }) {
  const eventTitle = invitation.event?.title ?? t("common.notAvailable")
  const inviterName = invitation.invited_by?.username ?? t("common.someone")

  return (
    <Link
      to={`/event/${invitation.event?.slug}`}
      className="flex items-start gap-2 w-full text-left px-3 py-2.5 rounded-lg hover:bg-muted/80 transition-colors bg-primary/5"
    >
      <NotificationTypeIcon type="event_invitation" />
      <div className="flex flex-col gap-0.5 min-w-0">
        <p className="text-sm font-medium line-clamp-1">{t("notifications.invitationTitle", { eventTitle })}</p>
        <p className="text-xs text-muted-foreground">{t("notifications.invitedYou", { inviterName })}</p>
        <p className="text-xs text-muted-foreground">
          {formatDistanceToNow(new Date(invitation.created_at), { addSuffix: true })}
        </p>
      </div>
    </Link>
  )
}

export default function NotificationBell({ isCollapsed = false }: { isCollapsed?: boolean }) {
  const { t } = useTranslation()
  const { currentUser } = useAuthSession()
  const navigate = useNavigate()
  const {
    dbNotifications,
    eventInvitations,
    unreadCount,
    isLoading,
    markRead,
    markAllRead,
    deleteNotification,
    isMarkingAllRead,
  } = useNotifications()

  const handleNotificationClick = async (n: DbNotification, link: string | null) => {
    if (isUnread(n)) {
      try {
        await markRead(n.id)
      } catch {
        // Ignore
      }
    }
    if (link) {
      navigate(link)
    }
  }

  if (!currentUser || isCollapsed) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="relative flex items-center justify-center w-8 h-8 rounded-lg hover:bg-primary/10 transition group"
          title={t("notifications.title")}
          aria-label={
            unreadCount > 0
              ? `${t("notifications.title")} (${unreadCount} unread)`
              : t("notifications.title")
          }
        >
          <Bell className="w-5 h-5 text-foreground-secondary group-hover:text-primary transition" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[11px] font-bold">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[calc(100vw-1rem)] max-w-[320px] sm:w-96 p-0">
        <div className="p-3 border-b border-border flex items-center justify-between gap-2">
          <h3 className="font-semibold text-sm">{t("notifications.title")}</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs px-2"
                disabled={isMarkingAllRead}
                onClick={() => markAllRead().catch(() => {})}
              >
                {t("notifications.markAllRead")}
              </Button>
            )}
            <Link to="/profile/notifications" className="text-xs text-primary hover:underline">
              {t("common.viewAll")}
            </Link>
          </div>
        </div>
        <div className="max-h-[min(400px,calc(100vh-150px))] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : dbNotifications.length === 0 && eventInvitations.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {t("notifications.empty")}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {eventInvitations.map((inv) => (
                <InvitationItem key={`inv-${inv.id}`} invitation={inv} t={t} />
              ))}
              {dbNotifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onSelect={() => handleNotificationClick(n, n.link)}
                  onDelete={() => deleteNotification(n.id).catch(() => {})}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
